import React, {useEffect, useMemo, useState} from 'react';
import _ from 'lodash';
import {
    Activity,
    BarChart3,
    Calendar,
    Clock,
    ExternalLink,
    Eye,
    Filter,
    Globe,
    MapPin,
    Monitor,
    Smartphone,
    Tablet,
    TrendingUp,
    Users
} from 'lucide-react';
import {useSocket} from "./SocketContext.jsx";

export default function RecentActivity() {
    const { recentVisits } = useSocket();

    // Sécuriser recentVisits avec lodash
    const safeRecentVisits = useMemo(() => {
        return _.isArray(recentVisits) ? recentVisits : [];
    }, [recentVisits]);

    const [filteredVisits, setFilteredVisits] = useState([]);
    const [filter, setFilter] = useState('all');
    const [timeRange, setTimeRange] = useState('today');
    const [stats, setStats] = useState({
        total: 0,
        unique: 0,
        countries: 0,
        devices: {}
    });

    // Appliquer les filtres avec lodash
    useEffect(() => {
        let result = [...safeRecentVisits];

        // Filtre par type d'appareil
        if (filter !== 'all') {
            result = _.filter(result, visit => {
                const deviceType = _.get(visit, 'deviceType', 'desktop');
                return deviceType === filter;
            });
        }

        // Filtre par période
        const now = new Date();
        if (timeRange === 'today') {
            const startOfDay = new Date(now.getFullYear(), now.getMonth(), now.getDate());
            result = _.filter(result, visit => {
                const visitedAt = _.get(visit, 'visitedAt');
                if (!visitedAt) return false;

                try {
                    const visitDate = new Date(visitedAt);
                    return visitDate >= startOfDay;
                } catch (error) {
                    return false;
                }
            });
        } else if (timeRange === 'hour') {
            const oneHourAgo = new Date(now.getTime() - 60 * 60 * 1000);
            result = _.filter(result, visit => {
                const visitedAt = _.get(visit, 'visitedAt');
                if (!visitedAt) return false;

                try {
                    const visitDate = new Date(visitedAt);
                    return visitDate >= oneHourAgo;
                } catch (error) {
                    return false;
                }
            });
        }

        setFilteredVisits(_.slice(result, 0, 20));

        // Calculer les statistiques
        calculateStats(result);
    }, [safeRecentVisits, filter, timeRange]);

    const calculateStats = (visits) => {
        const uniqueIPs = new Set();
        const countries = new Set();
        const devices = {
            desktop: 0,
            mobile: 0,
            tablet: 0
        };

        _.forEach(visits, visit => {
            const ip = _.get(visit, 'ip');
            const country = _.get(visit, 'country');
            const deviceType = _.get(visit, 'deviceType', 'desktop');

            if (ip) uniqueIPs.add(ip);
            if (country) countries.add(country);

            switch(deviceType) {
                case 'desktop': devices.desktop++; break;
                case 'mobile': devices.mobile++; break;
                case 'tablet': devices.tablet++; break;
                default: devices.desktop++;
            }
        });

        setStats({
            total: visits.length,
            unique: uniqueIPs.size,
            countries: countries.size,
            devices
        });
    };

    // Formater la date avec sécurité
    const formatTime = (dateString) => {
        if (!dateString) return 'Date inconnue';

        try {
            const date = new Date(dateString);
            if (_.isNaN(date.getTime())) return 'Date invalide';

            const now = new Date();
            const diffMs = now - date;
            const diffMins = Math.floor(diffMs / 60000);
            const diffHours = Math.floor(diffMs / 3600000);

            if (diffMins < 1) return 'À l\'instant';
            if (diffMins < 60) return `Il y a ${diffMins} min`;
            if (diffHours < 24) return `Il y a ${diffHours} h`;

            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } catch (error) {
            console.warn('Erreur formatage date:', error);
            return 'Date invalide';
        }
    };

    // Obtenir l'icône de l'appareil
    const getDeviceIcon = (deviceType) => {
        switch(deviceType) {
            case 'mobile': return Smartphone;
            case 'tablet': return Tablet;
            default: return Monitor;
        }
    };

    // Obtenir la couleur de l'appareil
    const getDeviceColor = (deviceType) => {
        switch(deviceType) {
            case 'mobile': return 'text-blue-600';
            case 'tablet': return 'text-purple-600';
            default: return 'text-gray-600';
        }
    };

    // Obtenir la couleur de fond de l'appareil
    const getDeviceBgColor = (deviceType) => {
        switch(deviceType) {
            case 'mobile': return 'bg-blue-20';
            case 'tablet': return 'bg-purple-20';
            default: return 'bg-gray-20';
        }
    };

    return (
        <div className="bg-white rounded-xl shadow overflow-hidden">
            {/* En-tête */}
            <div className="p-6 border-b border-gray-200">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h2 className="text-xl font-semibold text-gray-900 flex items-center">
                            <Activity className="h-5 w-5 mr-2" />
                            Activité Récente
                        </h2>
                        <p className="text-sm text-gray-500 mt-1">
                            Visites en temps réel sur vos sites
                        </p>
                    </div>

                    <div className="flex flex-wrap gap-3">
                        {/* Filtre période */}
                        <div className="flex items-center">
                            <Calendar className="h-4 w-4 text-gray-400 mr-2" />
                            <select
                                value={timeRange}
                                onChange={(e) => setTimeRange(e.target.value)}
                                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                            >
                                <option value="hour">Dernière heure</option>
                                <option value="today">Aujourd'hui</option>
                                <option value="all">Tout</option>
                            </select>
                        </div>

                        {/* Filtre appareil */}
                        <div className="flex items-center">
                            <Filter className="h-4 w-4 text-gray-400 mr-2" />
                            <select
                                value={filter}
                                onChange={(e) => setFilter(e.target.value)}
                                className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                            >
                                <option value="all">Tous les appareils</option>
                                <option value="desktop">Ordinateur</option>
                                <option value="mobile">Mobile</option>
                                <option value="tablet">Tablette</option>
                            </select>
                        </div>
                    </div>
                </div>
            </div>

            {/* Statistiques rapides */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 p-6 border-b border-gray-200">
                <div className="bg-blue-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-blue-700 mb-1">Total visites</p>
                            <p className="text-2xl font-bold text-blue-900">{stats.total}</p>
                        </div>
                        <Users className="h-8 w-8 text-blue-600 opacity-50" />
                    </div>
                </div>

                <div className="bg-green-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-green-700 mb-1">Visiteurs uniques</p>
                            <p className="text-2xl font-bold text-green-900">{stats.unique}</p>
                        </div>
                        <Eye className="h-8 w-8 text-green-600 opacity-50" />
                    </div>
                </div>

                <div className="bg-purple-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-purple-700 mb-1">Pays</p>
                            <p className="text-2xl font-bold text-purple-900">{stats.countries}</p>
                        </div>
                        <Globe className="h-8 w-8 text-purple-600 opacity-50" />
                    </div>
                </div>

                <div className="bg-orange-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                        <div>
                            <p className="text-sm text-orange-700 mb-1">Principal appareil</p>
                            <p className="text-lg font-bold text-orange-900">
                                {stats.devices.desktop >= stats.devices.mobile ? 'Desktop' : 'Mobile'}
                            </p>
                            <p className="text-xs text-orange-600">
                                {stats.devices.desktop}d / {stats.devices.mobile}m / {stats.devices.tablet}t
                            </p>
                        </div>
                        <BarChart3 className="h-8 w-8 text-orange-600 opacity-50" />
                    </div>
                </div>
            </div>

            {/* Liste des visites */}
            <div className="divide-y divide-gray-200">
                {filteredVisits.length > 0 ? (
                    filteredVisits.map((visit, index) => {
                        // Sécuriser les données de visite
                        const safeVisit = _.isObject(visit) ? visit : {};

                        const deviceType = _.get(safeVisit, 'deviceType', 'desktop');
                        const DeviceIcon = getDeviceIcon(deviceType);
                        const deviceColor = getDeviceColor(deviceType);
                        const deviceBgColor = getDeviceBgColor(deviceType);

                        const ip = _.get(safeVisit, 'ip', 'IP inconnue');
                        const country = _.get(safeVisit, 'country', '');
                        const city = _.get(safeVisit, 'city', 'Localisation inconnue');
                        const browser = _.get(safeVisit, 'browser', 'Navigateur inconnu');
                        const operatingSystem = _.get(safeVisit, 'operatingSystem', 'OS inconnu');
                        const pageTitle = _.get(safeVisit, 'pageTitle', '');
                        const referrer = _.get(safeVisit, 'referrer', '');
                        const siteName = _.get(safeVisit, 'siteName', '');
                        const visitedAt = _.get(safeVisit, 'visitedAt', '');

                        return (
                            <div key={`visit-${index}`} className="p-6 hover:bg-gray-50 transition-colors">
                                <div className="flex items-start justify-between">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center mb-2">
                                            <div className={`p-2 rounded-lg ${deviceBgColor}`}>
                                                <DeviceIcon className={`h-4 w-4 ${deviceColor}`} />
                                            </div>
                                            <div className="ml-3">
                                                <div className="flex items-center">
                                                    <span className="text-sm font-medium text-gray-900 truncate">
                                                        {ip}
                                                    </span>
                                                    {country && (
                                                        <span className="ml-2 px-2 py-0.5 text-xs bg-gray-100 text-gray-700 rounded-full">
                                                            {country}
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="flex items-center text-xs text-gray-500 mt-1">
                                                    <MapPin className="h-3 w-3 mr-1" />
                                                    <span className="truncate">
                                                        {city}
                                                        {country && `, ${country}`}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="ml-12 space-y-2">
                                            <div className="flex flex-wrap items-center gap-2">
                                                <span className="text-sm text-gray-600">
                                                    {browser}
                                                </span>
                                                <span className="text-gray-400">•</span>
                                                <span className="text-sm text-gray-600">
                                                    {operatingSystem}
                                                </span>
                                                {pageTitle && (
                                                    <>
                                                        <span className="text-gray-400">•</span>
                                                        <span className="text-sm text-gray-500 truncate max-w-xs">
                                                            {_.truncate(pageTitle, { length: 50 })}
                                                        </span>
                                                    </>
                                                )}
                                            </div>

                                            {referrer && (
                                                <div className="flex items-center text-xs text-gray-500">
                                                    <span className="mr-2">Referrer:</span>
                                                    <a
                                                        href={referrer}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className="text-blue-600 hover:text-blue-800 truncate flex items-center"
                                                    >
                                                        {referrer.replace(/^https?:\/\//, '')}
                                                        <ExternalLink className="h-3 w-3 ml-1" />
                                                    </a>
                                                </div>
                                            )}
                                        </div>
                                    </div>

                                    <div className="ml-4 flex flex-col items-end">
                                        <div className="flex items-center text-sm text-gray-500 mb-2">
                                            <Clock className="h-3 w-3 mr-1" />
                                            <span>{formatTime(visitedAt)}</span>
                                        </div>

                                        {siteName && (
                                            <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                                <Globe className="h-2.5 w-2.5 mr-1" />
                                                {_.truncate(siteName, { length: 20 })}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="p-12 text-center">
                        <div className="flex flex-col items-center">
                            <Activity className="h-12 w-12 text-gray-400 mb-4" />
                            <p className="text-gray-500 text-lg font-medium mb-2">
                                Aucune activité récente
                            </p>
                            <p className="text-gray-400 text-sm">
                                Les visites apparaîtront ici en temps réel
                            </p>
                        </div>
                    </div>
                )}
            </div>

            {/* Pied de page */}
            {filteredVisits.length > 0 && (
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                    <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-500">
                            Affichage de {filteredVisits.length} visite{filteredVisits.length !== 1 ? 's' : ''}
                            {safeRecentVisits.length > filteredVisits.length && ` sur ${safeRecentVisits.length}`}
                        </div>
                        <div className="flex items-center text-sm text-blue-600">
                            <TrendingUp className="h-4 w-4 mr-1" />
                            <span>Temps réel</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
